import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { Box, TableCell, TableRow, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TimelineIcon from 'components/TimelineIcon';
import { CalendarToday } from '@mui/icons-material';
import EmptyState from 'components/EmptyState';
import { ServiceOrder, SummaryServiceOrder } from 'types/serviceOrder.types';
import { Account } from 'types/Account';

function ServiceOrdersRows({
  data,
  loading,
  accountSelected,
}: {
  data: ServiceOrder[] | undefined;
  loading: boolean;
  accountSelected: Account | null;
}) {
  const { t } = useTranslation(['_common', 'service_order']);

  const haveData = data && data.length > 0;
  const isEmpty = !loading && !haveData;

  return (
    <>
      {loading && <ServiceOrdersRowsLoading accountSelected={accountSelected} />}
      {haveData &&
        data.map((serviceOrder) => (
          <TableRow
            key={serviceOrder.id}
            component={Link}
            to={`/service-orders/${serviceOrder.id}${
              accountSelected ? '' : `/${(serviceOrder as SummaryServiceOrder).accountId}`
            }`}
            sx={{
              textDecoration: 'none',
            }}
            hover
          >
            <TableCell sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimelineIcon
                  type={serviceOrder.type}
                  IconProps={{ sx: { height: '0.8rem', width: '0.8rem' } }}
                />
                {(serviceOrder.sequential ? `#${serviceOrder.sequential} - ` : '') +
                  serviceOrder.description || t('_common:service_order')}
              </Box>
            </TableCell>
            {!accountSelected && (
              <TableCell sx={{ py: 1, overflow: 'hidden' }}>
                {(serviceOrder as SummaryServiceOrder).accountName} (
                {(serviceOrder as SummaryServiceOrder).accountCode})
              </TableCell>
            )}
            <TableCell sx={{ py: 1 }}>
              {/* @ts-ignore */}
              {t(`service_order:${String(serviceOrder.status)}`)}
            </TableCell>
            <TableCell sx={{ py: 1 }}>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ width: '0.8rem', mr: 0.5 }} />
                {dayjs(serviceOrder.date).format('L LTS')}
              </Box>
            </TableCell>
            <TableCell sx={{ py: 1 }}>
              {serviceOrder.endDate && (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarToday sx={{ width: '0.8rem', mr: 0.5 }} />
                  {dayjs(serviceOrder.endDate).format('L LTS')}
                </Box>
              )}
            </TableCell>
            <TableCell sx={{ py: 1 }}>{serviceOrder.executant}</TableCell>
          </TableRow>
        ))}
      {isEmpty && (
        <EmptyState
          sx={{
            position: 'absolute',
            left: '0',
            right: '0',
            bottom: '0',
            top: '0',
          }}
          title={t('_common:there_is_still_nothing_here')}
          description={t('service_order:there_is_no_service_order_for_this_account_in_this_period')}
        />
      )}
    </>
  );
}

function ServiceOrdersRowsLoading({ accountSelected }: { accountSelected: Account | null }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton />
          </TableCell>
          {!accountSelected && (
            <TableCell>
              <Skeleton />
            </TableCell>
          )}
          <TableCell>
            <Skeleton />
          </TableCell>
          <TableCell>
            <Skeleton />
          </TableCell>
          <TableCell>
            <Skeleton />
          </TableCell>
          <TableCell>
            <Skeleton />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export default ServiceOrdersRows;
